import { API_BASE_URL } from '../../../../../config';
import { FORM_CODE_FF017 } from './siteArrivalConfirmationTypes';
import type { SiteArrivalConfirmationFormValues } from './siteArrivalConfirmationTypes';

export const DONE_STEP_ID = '__done__';

export type SiteArrivalConfirmationProgressPayload = {
  values: SiteArrivalConfirmationFormValues;
  currentStepId: string | null;
};

type ProgressJson = {
  travelReceiptAcknowledged?: string;
  healthCheckupDate?: string;
  healthCheckupLocation?: string;
  companyAccommodation?: string;
  rentTv?: string;
  rentFuton?: string;
  rentFridge?: string;
  rentOther?: string;
  agreeDorm?: string;
  breakfast?: string;
  lunch?: string;
  dinner?: string;
  agreeMeal?: string;
  consentDateFf017?: string;
  currentStepId?: string | null;
};

function mapResponseToValues(data: ProgressJson): SiteArrivalConfirmationFormValues {
  const values: SiteArrivalConfirmationFormValues = {};
  if (data.travelReceiptAcknowledged === 'yes' || data.travelReceiptAcknowledged === 'no') {
    values.travelReceiptAcknowledged = data.travelReceiptAcknowledged;
  }
  if (data.healthCheckupDate?.trim()) values.healthCheckupDate = data.healthCheckupDate.trim();
  if (data.healthCheckupLocation != null) values.healthCheckupLocation = data.healthCheckupLocation;
  if (data.companyAccommodation === 'yes' || data.companyAccommodation === 'no') {
    values.companyAccommodation = data.companyAccommodation;
  }
  if (data.rentTv === 'yes' || data.rentTv === 'no') values.rentTv = data.rentTv;
  if (data.rentFuton === 'yes' || data.rentFuton === 'no') values.rentFuton = data.rentFuton;
  if (data.rentFridge === 'yes' || data.rentFridge === 'no') values.rentFridge = data.rentFridge;
  if (data.rentOther != null) values.rentOther = data.rentOther;
  if (data.agreeDorm === 'true' || data.agreeDorm === 'false') values.agreeDorm = data.agreeDorm;
  if (data.breakfast === 'yes' || data.breakfast === 'no') values.breakfast = data.breakfast;
  if (data.lunch === 'yes' || data.lunch === 'no') values.lunch = data.lunch;
  if (data.dinner === 'yes' || data.dinner === 'no') values.dinner = data.dinner;
  if (data.agreeMeal === 'true' || data.agreeMeal === 'false') values.agreeMeal = data.agreeMeal;
  if (data.consentDateFf017?.trim()) values.consentDateFf017 = data.consentDateFf017.trim();
  return values;
}

function valuesToBody(values: SiteArrivalConfirmationFormValues): Record<string, string> {
  const body: Record<string, string> = {};
  if (values.travelReceiptAcknowledged) body.travelReceiptAcknowledged = values.travelReceiptAcknowledged;
  if (values.healthCheckupDate?.trim()) body.healthCheckupDate = values.healthCheckupDate.trim();
  if (values.healthCheckupLocation != null) body.healthCheckupLocation = values.healthCheckupLocation;
  if (values.companyAccommodation) body.companyAccommodation = values.companyAccommodation;
  if (values.rentTv) body.rentTv = values.rentTv;
  if (values.rentFuton) body.rentFuton = values.rentFuton;
  if (values.rentFridge) body.rentFridge = values.rentFridge;
  if (values.rentOther != null) body.rentOther = values.rentOther;
  if (values.agreeDorm === 'true' || values.agreeDorm === 'false') body.agreeDorm = values.agreeDorm;
  if (values.breakfast) body.breakfast = values.breakfast;
  if (values.lunch) body.lunch = values.lunch;
  if (values.dinner) body.dinner = values.dinner;
  if (values.agreeMeal === 'true' || values.agreeMeal === 'false') body.agreeMeal = values.agreeMeal;
  return body;
}

export async function fetchSiteArrivalConfirmationProgress(
  email: string,
  invitationId: string
): Promise<SiteArrivalConfirmationProgressPayload | null> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim()) return null;
  const params = new URLSearchParams({
    email: email.trim(),
    invitationId: invitationId.trim(),
    formCode: FORM_CODE_FF017,
  });
  const r = await fetch(`${API_BASE_URL}/api/pre-employment-checklist/progress?${params.toString()}`);
  if (!r.ok) return null;
  const data = (await r.json()) as ProgressJson;
  return {
    values: mapResponseToValues(data),
    currentStepId: data.currentStepId ?? null,
  };
}

export async function upsertSiteArrivalConfirmationProgress(params: {
  email: string;
  invitationId: string;
  values: SiteArrivalConfirmationFormValues;
  currentStepId: string | null;
}): Promise<SiteArrivalConfirmationProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim() || !params.invitationId?.trim()) return null;
  try {
    const body: Record<string, string | null> = {
      email: params.email.trim(),
      invitationId: params.invitationId.trim(),
      formCode: FORM_CODE_FF017,
      currentStepId: params.currentStepId,
      ...valuesToBody(params.values),
    };
    const r = await fetch(`${API_BASE_URL}/api/pre-employment-checklist/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      console.warn('Site arrival confirmation save failed', r.status);
      return null;
    }
    const data = (await r.json()) as ProgressJson;
    const values = { ...params.values, ...mapResponseToValues(data) };
    return {
      values,
      currentStepId: data.currentStepId ?? params.currentStepId,
    };
  } catch (e) {
    console.warn('Site arrival confirmation save failed', e);
    return null;
  }
}
